import { EntityInfo, EntityInfoUtils } from '@record-it-npm/synchro-common';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { Disposable } from 'record-it-utils/utils/DisposableContainer/DisposableContainer';
import { PromiseContainer } from 'record-it-utils/utils/PromiseContainer/PromiseContainer';
import { AnyEntitiesWithoutEntityManagerSpecificFieldsWithEntityName } from '../AnyEntitiesWithoutEntityManagerSpecificFieldsWithEntityName';
import { EntityManagerBaseEntity } from '../EntityManagerBaseEntity';
import { EntityManagerEntityTypesByEntityName } from '../EntityManagerEntityTypesByEntityName';
import { EntityRepository } from '../EntityRepository/EntityRepository';
import { RepositoryByEntityName, EntityRepositoryContainer } from '../EntityRepositoryContainer/EntityRepositoryContainer';
import { HooksContainer } from '../HooksContainer/HooksContainer';
import { AnySynchronizationEnvironmentTypesConstraint } from '../SynchronizationEnvironmentTypes';
import { EntityActualizationInfo, LastActualizationInfo } from './EntityActualizationInfo';
import { EntityRevisionManager } from './EntityRevisionManager';
export class EntityActualization<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint, TEntityTypesByEntityName extends EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>, TRepositoryByEntityName extends RepositoryByEntityName<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>> {
  private actualizing: boolean = false;
  private actualizingPromiseContainer: PromiseContainer<void> = new PromiseContainer();
  private entityRevisionManager: EntityRevisionManager<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName, TRepositoryByEntityName>;
  private readonly entityActualizationInfo = new EntityActualizationInfo<TSynchronizationEnvironmentTypes>();
  private hooksContainer: HooksContainer<EntityActualizationHooks<TSynchronizationEnvironmentTypes>> = new HooksContainer();
  private relativeSynchronizationEntityIdsByEntityName: RelativeSynchronizationEntityIdsByEntityName<TSynchronizationEnvironmentTypes> = new Map();
  private postActualizationTasks: Set<PostActualizationTask<TSynchronizationEnvironmentTypes>> = new Set();
  constructor(private readonly repositoryContainer: EntityRepositoryContainer<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName, TRepositoryByEntityName>, private readonly getActualizationsFunction: GetActualizationsFunction<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>) {
    this.entityRevisionManager = new EntityRevisionManager(repositoryContainer);
  }
  public async init(): Promise<void> {
    await this.entityRevisionManager.init();
    await this.entityActualizationInfo.init();
  }
  public destroy(): void {
    this.entityRevisionManager.destroy();
  }
  public async actualize(): Promise<void> {
    const promise = this.actualizingPromiseContainer.create();
    if (!this.actualizing) {
      void this.startActualizationLoop();
    }
    await promise;
  }
  public async flush(): Promise<void> {
    await this.entityRevisionManager.flush();
  }
  public bindIsActualizing(callback: (isActualizing: boolean) => void): Disposable {
    const callCallback = (): void => {
      callback(this.isActualizing());
    };
    callCallback();
    return this.registerHooks({
      isActualizingChanged: callCallback
    });
  }
  public registerHooks(hooks: EntityActualizationHooks<TSynchronizationEnvironmentTypes>): Disposable {
    return this.hooksContainer.registerHooks(hooks);
  }

  /**
   * Calls the task after the actualizations were retrieved from the server.
   *
   * @param postActualizationTask
   * @returns
   */
  public registerPostActualizationTask(postActualizationTask: PostActualizationTask<TSynchronizationEnvironmentTypes>): Disposable {
    this.postActualizationTasks.add(postActualizationTask);
    return {
      dispose: () => {
        this.postActualizationTasks.delete(postActualizationTask);
      }
    };
  }
  public isActualizing(): boolean {
    return this.actualizing;
  }
  public addRelativeSynchronizationEntityId(entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'], entityId: EntityManagerBaseEntity['id']): void {
    let set = this.relativeSynchronizationEntityIdsByEntityName.get(entityName);
    if (!set) {
      set = new Set();
      this.relativeSynchronizationEntityIdsByEntityName.set(entityName, set);
    }
    set.add(entityId);
    this.hooksContainer.callHooks('relativeSynchronizationEntityIdAdded', {
      entityName,
      entityId
    });
  }
  public removeRelativeSynchronizationEntityId(entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'], entityId: EntityManagerBaseEntity['id']): void {
    const set = this.relativeSynchronizationEntityIdsByEntityName.get(entityName);
    if (set) {
      set.delete(entityId);
    }
    this.entityRevisionManager.resetRelativeRevision(entityName, entityId);
    this.hooksContainer.callHooks('relativeSynchronizationEntityIdRemoved', {
      entityName,
      entityId
    });
  }
  public getRelativeSynchronizationEntityIdsByEntityName(entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']): Array<string> {
    const set = this.relativeSynchronizationEntityIdsByEntityName.get(entityName);
    if (!set) {
      return [];
    }
    return Array.from(set.values());
  }
  public getLastSuccessfulActualizationInfo(): LastActualizationInfo<TSynchronizationEnvironmentTypes> | null {
    return this.entityActualizationInfo.getLastSuccessfulActualizationInfo();
  }
  private async startActualizationLoop(): Promise<void> {
    this.setActualizing(true);
    for (;;) {
      const startPromiseCount = this.actualizingPromiseContainer.getPendingPromiseCount();
      try {
        const relativeSynchronizationEntityIdsByEntityName = this.createRelativeSynchronizationEntityIdsByEntityNameCopy();
        await this.retrieveAllActualizations();
        await this.executePostActualizationTasks(relativeSynchronizationEntityIdsByEntityName);
        this.entityActualizationInfo.actualizationFinishedSuccessfully({
          relativeSynchronizationEntityIdsByEntityName
        });
        this.hooksContainer.callHooks('afterActualization', {
          success: true
        });
      } catch (e) {
        if (e instanceof Error) {
          this.actualizingPromiseContainer.rejectAll(e);
        } else {
          this.actualizingPromiseContainer.rejectAll(new Error(`Actualization Error: ${String(e)}`));
        }
        this.hooksContainer.callHooks('afterActualization', {
          success: false
        });
        break;
      }

      // if the count is not equal, it means that a new actualization was requested, so we start another round
      if (this.actualizingPromiseContainer.getPendingPromiseCount() === startPromiseCount) {
        this.actualizingPromiseContainer.resolveAll();
        break;
      }
    }
    this.setActualizing(false);
  }
  private setActualizing(actualizing: boolean): void {
    if (actualizing !== this.actualizing) {
      this.actualizing = actualizing;
      this.hooksContainer.callHooks('isActualizingChanged');
    }
  }
  private async retrieveAllActualizations(): Promise<void> {
    let missingEntityInfos = this.getEntityInfos();
    for (;;) {
      const actualizationResult = await this.getActualizationsFunction(missingEntityInfos);
      await this.processReceivedActualizations(actualizationResult.entities);
      if (actualizationResult.missingEntityInfos.length) {
        ({
          missingEntityInfos
        } = actualizationResult);
      } else {
        break;
      }
    }
  }
  private async executePostActualizationTasks(relativeSynchronizationEntityIdsByEntityName: RelativeSynchronizationEntityIdsByEntityName<TSynchronizationEnvironmentTypes>): Promise<void> {
    for (const task of this.postActualizationTasks.values()) {
      await task(relativeSynchronizationEntityIdsByEntityName);
    }
  }
  private async processReceivedActualizations(entities: Array<AnyEntitiesWithoutEntityManagerSpecificFieldsWithEntityName<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>>): Promise<void> {
    for (const entityData of entities) {
      const repository = this.repositoryContainer.getByEntityName(entityData.entityName) as EntityRepository<TSynchronizationEnvironmentTypes, any, any>;
      for (const entity of entityData.entities) {
        if (!entity.deleted) {
          repository.addExternalEntityLocally(entity);
        } else {
          repository.removeLocally(entity);
        }
      }
      this.entityRevisionManager.processActualizedEntities(entityData);
    }
  }
  private getEntityInfos(): Array<GetActualizationEntityInfo<TSynchronizationEnvironmentTypes>> {
    return this.repositoryContainer.getAll().map(repository => {
      const {
        entityName
      } = repository.getEntityInfo();
      return {
        entityName,
        revision: this.entityRevisionManager.getRevision(entityName),
        entitySpecificRevisions: this.getEntitySpecificRevisions(entityName)
      };
    });
  }
  private getEntitySpecificRevisions<TEntityName extends TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']>(entityName: TEntityName): Array<GetActualizationEntitySpecificRevision<TSynchronizationEnvironmentTypes>> {
    const entityInfo = this.repositoryContainer.getByEntityName(entityName).getEntityInfo() as EntityInfo<TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'], TEntityName, TEntityTypesByEntityName[TEntityName]['entity']>;
    const relativeSynchronizationFieldInfos = EntityInfoUtils.getAllRelativeSynchronizationFieldInfos<TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'], TEntityName, any>(entityInfo);
    const specificRevisions: Array<GetActualizationEntitySpecificRevision<TSynchronizationEnvironmentTypes>> = [];
    for (const relativeEntityName of entityInfo.synchronizeRelativeTo) {
      if (entityName !== relativeEntityName) {
        const fieldInfo = relativeSynchronizationFieldInfos.find(info => info.ref === relativeEntityName);
        assertNotNullOrUndefined(fieldInfo, `${entityName} no relativeSynchronization field info found for ${relativeEntityName}`);
      }
      specificRevisions.push(...this.getSpecificRevisionsForEntityName(entityName, relativeEntityName));
    }
    return specificRevisions;
  }
  private getSpecificRevisionsForEntityName(entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'], relativeEntityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']): Array<GetActualizationEntitySpecificRevision<TSynchronizationEnvironmentTypes>> {
    const referencedEntities = this.repositoryContainer.getByEntityName(relativeEntityName).getAllIncludingInvisibleEntities();
    const includedEntityIds = this.relativeSynchronizationEntityIdsByEntityName.get(relativeEntityName);
    const specificRevisions: Array<GetActualizationEntitySpecificRevision<TSynchronizationEnvironmentTypes>> = [];
    for (const referencedEntity of referencedEntities) {
      if (referencedEntity.onlyLocal || !includedEntityIds?.has(referencedEntity.id)) {
        continue;
      }
      specificRevisions.push({
        entityName: relativeEntityName,
        entityId: referencedEntity.id,
        revision: this.entityRevisionManager.getRelativeRevision(entityName, relativeEntityName, referencedEntity.id)
      });
    }
    return specificRevisions;
  }
  private createRelativeSynchronizationEntityIdsByEntityNameCopy(): RelativeSynchronizationEntityIdsByEntityName<TSynchronizationEnvironmentTypes> {
    // create a copy so new values won't sneak into the last info
    const relativeSynchronizationEntityIdsByEntityName: RelativeSynchronizationEntityIdsByEntityName<TSynchronizationEnvironmentTypes> = new Map();
    for (const [key, value] of this.relativeSynchronizationEntityIdsByEntityName.entries()) {
      relativeSynchronizationEntityIdsByEntityName.set(key, new Set(value));
    }
    return relativeSynchronizationEntityIdsByEntityName;
  }
}
export type GetActualizationsFunction<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint, TEntityTypesByEntityName extends EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>> = (infos: Array<GetActualizationEntityInfo<TSynchronizationEnvironmentTypes>>) => Promise<GetActualizationsResult<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>>;
export type GetActualizationEntityInfo<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint, TEntityName extends TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'] = TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']> = {
  entityName: TEntityName;
  revision: number;
  entitySpecificRevisions: Array<GetActualizationEntitySpecificRevision<TSynchronizationEnvironmentTypes>>;
};
export type GetActualizationEntitySpecificRevision<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint> = {
  entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'];
  entityId: EntityManagerBaseEntity['id'];
  revision: number;
};
export type GetActualizationsResult<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint, TEntityTypesByEntityName extends EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>> = {
  entities: Array<AnyEntitiesWithoutEntityManagerSpecificFieldsWithEntityName<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>>;
  missingEntityInfos: Array<GetActualizationEntityInfo<TSynchronizationEnvironmentTypes>>;
};
export type EntityActualizationHooks<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint> = {
  afterActualization?: (options: {
    success: boolean;
  }) => void;
  isActualizingChanged?: () => void;
  relativeSynchronizationEntityIdAdded?: (options: {
    entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'];
    entityId: EntityManagerBaseEntity['id'];
  }) => void;
  relativeSynchronizationEntityIdRemoved?: (options: {
    entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'];
    entityId: EntityManagerBaseEntity['id'];
  }) => void;
};
export type RelativeSynchronizationEntityIdsByEntityName<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint> = Map<TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'], Set<EntityManagerBaseEntity['id']>>;
type PostActualizationTask<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint> = (relativeSynchronizationEntityIdsByEntityName: RelativeSynchronizationEntityIdsByEntityName<TSynchronizationEnvironmentTypes>) => Promise<void>;